import React from 'react';
import { Skeleton } from "@/components/ui/skeleton";
import { useAppSelector } from 'redux/hooks';
import { formatIndianNumber } from 'utils';

interface CalculationPanelProps {
  isCalcLoading: boolean;
}

const CalculationPanel: React.FC<CalculationPanelProps> = ({ isCalcLoading }) => {
  const {
    cal_customer_rate,
    cal_amount_inr,
    amount,
    currency,
    cal_total_charges,
    cal_gst,
    cal_foreign_bank_charges,
    tcs,
    cal_tt_charges,
    cal_total_payable,
  } = useAppSelector((state) => state.CreateDeal);

  const CalculationRow = ({ 
    label, 
    value, 
    isLoading = isCalcLoading,
    className = ""
  }: { 
    label: string; 
    value: string; 
    isLoading?: boolean;
    className?: string;
  }) => (
    <div className={`flex justify-between w-full ${className}`}>
      <span className="text-xs leading-[14.5px] indent-3.5 text-[#666666]">
        {label}
      </span>
      {isLoading ? (
        <Skeleton className="h-4 bg-gray-600 w-[100px] rounded-full" />
      ) : (
        <span className="text-sm font-semibold float-right leading-4 indent-3.5 text-[#333333]">
          {value}
        </span>
      )}
    </div>
  );

  return (
    <section className="flex-1 bg-white-A700 px-3 py-5 rounded-xl gap-4 flex flex-col">
      <p className="text-lg leading-4 font-semibold">Calculations</p>
      <div className="pt-5 pb-10 px-5 border-b-8 rounded-xl bg-custom-calc-gradient border-blue-700 gap-10">
        <div className="divide-y divide-neutral-400_1 gap-5 flex flex-col">
          <div className="flex flex-col gap-4 w-full">
            <CalculationRow
              label="Customer Rate"
              value={cal_customer_rate === "" ? "0.00" : `₹ ${cal_customer_rate}`}
            />
            
            <CalculationRow
              label="Recipient gets"
              value={
                cal_amount_inr === ""
                  ? "0.00"
                  : (currency === "" || currency === undefined
                      ? "USD "
                      : `${currency} `) +
                    formatIndianNumber(parseFloat(amount)?.toFixed(4)).toString()
              }
            />
            
            <div className="w-full border-[.3px]" />
            
            <div className="flex flex-col w-full gap-4">
              <CalculationRow
                label="Total Charges"
                value={
                  cal_total_charges === "" || amount === ""
                    ? "0.00"
                    : `INR ${parseFloat(Number(cal_total_charges)?.toFixed(2))}`
                }
                className="text-sm"
              />
              
              <div className="ml-4 border-l border-gray-300 flex flex-col gap-4 mt-2">
                <CalculationRow
                  label="GST on FX conversion"
                  value={
                    cal_gst === "" || amount === ""
                      ? "0.00"
                      : `INR ${Number(cal_gst)?.toFixed(2)}`
                  }
                />
                
                <CalculationRow
                  label="Foreign Bank charges (incl. GST)"
                  value={
                    cal_foreign_bank_charges === "" || amount === ""
                      ? "0.00"
                      : `INR ${Number(cal_foreign_bank_charges)?.toFixed(2)}`
                  }
                />
                
                {Number(tcs) > 0 && (
                  <CalculationRow
                    label="TCS"
                    value={
                      tcs === "" || amount === ""
                        ? "0.00"
                        : `INR ${Number(tcs)?.toFixed(2)}`
                    }
                  />
                )}
                
                <CalculationRow
                  label="TT charges (incl. GST)"
                  value={
                    cal_tt_charges === "" || amount === ""
                      ? "0.00"
                      : `INR ${Number(cal_tt_charges)?.toFixed(2)}`
                  }
                />
              </div>
            </div>
          </div>
          
          <div className="flex justify-between pt-2.5 text-blue-700 w-full">
            <span className="text-xs leading-[14.5px] indent-3.5 font-semibold">
              Total estimation
            </span>
            {isCalcLoading ? (
              <Skeleton className="h-4 bg-gray-600 w-[100px] rounded-full" />
            ) : (
              <span className="text-sm font-semibold float-right leading-4 indent-3.5 text-[#333333]">
                {cal_total_payable === "" || amount === ""
                  ? "0.00"
                  : `INR ${formatIndianNumber(parseFloat(cal_total_payable)?.toFixed(2))}`}
              </span>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CalculationPanel;